import Immutable from 'immutable';
import { createModelForState } from '../../modules/moduleregistry';

const EMPTY_MAP = Immutable.Map({});
const EMPTY_ARRAY = [];


var getProperty = function(imObj, path) {
	if (Array.isArray(path)) {
		if (path.length === 0) {
			return imObj;
		} else {
			return imObj.getIn(path);
		}
	} else {
		return imObj.get(path);
	}
};

var setProperty = function(imObj, path, value) {
	if (Array.isArray(path)) {
		return imObj.setIn(path, value);
	} else {
		return imObj.set(path, value);
	}
};


function containsActiveValue(object) {
	if (!object) {
		return false;
	}
	return object.some((value, key) => {
		if (Immutable.isMap(value)) {
			return containsActiveValue(value);
		} else if (Immutable.isList(value)) {
			return value.size > 0;
		} else {
			return !!value;
		}
	});

}

export default class RenderContext {
	updateData(forceReload) {
		if (forceReload) {
			this._rootData = this._binder.getData();
		}
		this._data = (this._basePath.length > 0 ? this._rootData.getIn(this._basePath) : this._rootData);
		if (this._errorPath) {
			this._rootErrors = this._rootData.getIn(this._errorPath) || EMPTY_MAP;
			this._errors = this._rootErrors.getIn(this._basePath) || EMPTY_MAP;
		}
		if (this._readOnlyPath) {
			this._rootReadOnlyMap = this._rootData.getIn(this._readOnlyPath) || EMPTY_MAP;
			this._readOnlyMap = this._rootReadOnlyMap.getIn(this._basePath) || EMPTY_MAP;
			if (this._readOnlyMap === true) {
				this._readOnlyMap = EMPTY_MAP;
				this._fixedReadOnly = true;
			}

		}
	}

	constructor(binder, _basePath, _rootData, _calcProperties, _onBeforeSetProperty) {
		this._calcProperties = _calcProperties || {};
		this._binder = binder;
		this._basePath = _basePath || [];
		this._rootData = _rootData || binder.getData() || EMPTY_MAP;
		this._errorPath = binder.getErrorPath();
		this._readOnlyPath = binder.getReadOnlyPath();
		this._onBeforeSetProperty = _onBeforeSetProperty;
		this.updateData();
	}

	refresh() {
		this.updateData(true);
		return this;
	}

	trigger(eventType, params) {
		this._binder.trigger(eventType, params);
	}

	hasErrors() {
		return containsActiveValue(this.getErrors());
	}

	withCalculatedProperties(value) {
		if (typeof value === 'function') {
			value = value(this);
		}
		this._calcProperties = value;
		return this;
	}

	setData(value) {
		this._rootData = this._binder.getData().setIn(this._basePath, value);
		this.updateData();
		this._binder.setData(this._rootData);

	}

	getData() {
		return this._data;
	}

	setRootErrors(errors) {
		if (this._errorPath) {
			this.setRootProperty(this._errorPath, errors);
		}
	}

	getRootErrors() {
		return this._rootErrors || EMPTY_MAP;
	}

	getProperty(path) {
		if (!this._data) {
			return undefined;
		}
		return getProperty(this._data, path);
	}

	getBasePathLeaf() {
		return this._basePath[this._basePath.length - 1];
	}


	getErrors() {
		return this._errors;
	}

	getError(path) {
		if (!this._errors) {
			return null;
		}
		return getProperty(this._errors, path);
	}

	getRootError(path) {
		if (!this._errorPath) {
			return null;
		}
		return getProperty(this._rootErrors, path);
	}

	getModel(path) {
		if (path) {
			return createModelForState(this.getProperty(path));
		} else {
			return this._binder.getModel();
		}
	}

	setModel(path, model) {
		if (model === undefined) { // called as setModel(model)
			this._binder.setModel(path);
		} else {
			this.setProperty(path, model.getState());
		}
	}


	setProcessSetProperty(processSetProperty) {
		this._processSetProperty = processSetProperty;
	}

	getBasePath() {
		return this._basePath.slice(0);
	}

	setOnBeforeSetProperty(value) {
		this._onBeforeSetProperty = value;
	}

	setProperty(path, value) {
		this._rootData = this._binder.getData();
		if (this._onBeforeSetProperty) {
			this._rootData = this._onBeforeSetProperty(this._rootData) || this._rootData;
		}
		this._rootData = this._rootData.setIn(this._basePath.concat(path), value);
		this.updateData();
		this._binder.setData(this._rootData);
	}

	getRootProperty(path) {
		return getProperty(this._rootData, path);
	}

	getCalcProperty(name) {
		return this._calcProperties ? this._calcProperties[name] : undefined;
	}

	getCalcProperties() {
		return this._calcProperties;
	}

	dumpCalcProperties() {
		console.log('calcProperties = ', this._calcProperties);
	}

	mergeCalcProperties(props) {
		if (props) {
			this._calcProperties = Object.assign({}, this._calcProperties, props);
		}
	}

	setRootProperty(path, value) {
		this._rootData = setProperty(this._binder.getData(), path, value);
		this.updateData();
		this._binder.setData(this._rootData);
	}

	withAppendedBasePath(basePath) {
		let newPath;
		if (Array.isArray(basePath) && basePath[0] === '/') {
			newPath = basePath.slice(1);
		} else {
			newPath = this._basePath.concat(basePath);
		}
		return new RenderContext(this._binder, newPath, this._rootData, this._calcProperties, this._onBeforeSetProperty)
						.setReadOnly(this.isContextReadOnly()).setParent(this);
	}

	clone() {
		return this.withAppendedBasePath([]);
	}

	setReadOnly(value) {
		this._explicitReadOnly = value;
		return this;
	}

	setParent(value) {
		this._parent = value;
		return this;
	}

	getParent() {
		return this._parent;
	}

	isContextReadOnly() {
		return this._explicitReadOnly || this._fixedReadOnly || this._inheritedReadOnly;
	}

	isReadOnly(path) {
		return this.isContextReadOnly() || (this._readOnlyMap && getProperty(this._readOnlyMap, path) === true);
	}

	isRootReadOnly(path) {
		return this._explicitReadOnly || (this._rootReadOnlyMap && getProperty(this._rootReadOnlyMap, path) === true);
	}



	withShortenedBasePath(removeCount) {
		if (removeCount === 0) {
			return this;
		}
		return new RenderContext(this._binder, this._basePath.slice(0, this._basePath.length - removeCount), this._rootData, this._calcProperties, this._onBeforeSetProperty);
	}

	getFullPath(path, isRootPath) {
		if (isRootPath) {
			return path || [];
		}
		if (!path) {
			return this._basePath;
		}
		return this._basePath.concat(path);
	}

	getLookup(name, fixedParams) {
		return this._binder.getLookup(name, fixedParams);
	}

}

